import type {
  AnsweredQuestionnaire,
  ConsentStatusUpdateRequest,
  MedicationsResponse,
  SoleraUserResponse,
  UserProfileResponse
} from "@9amhealth/openapi";
import {
  ConsentControllerService,
  ConsentStatusResponse,
  DemoControllerService,
  MedicationsResponseItem,
  ProfileControllerService,
  QuestionnaireControllerService,
  SmsControllerService,
  SoleraControllerService,
  UserProfileControllerService
} from "@9amhealth/openapi";
import { Cubit } from "blac";
import asTemplateValue from "lib/asTemplateValue";
import type { Condition } from "src/constants/conditions";
import { globalEvents } from "src/constants/globalEvents";
import {
  BEHAVIORAL_HEALTH_QUESTIONNAIRE_GAD7,
  BEHAVIORAL_HEALTH_QUESTIONNAIRE_PHQ8
} from "@9amhealth/shared";
import { PTHW_BINARY_RATING_QUESTIONNAIRE_ID } from "src/constants/misc";
import { PayerAdditionalLegalTextKey, PayerId } from "src/constants/payers";
import { AuthenticationMethod } from "src/ui/components/WithAuth/WithAuthCubit";
import { ProfileProgramMembership } from "src/constants/programs";
import { addSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import envVariables from "src/lib/envVariables";
import { extractErrorCode } from "src/lib/errors";
import { featureFlags } from "src/lib/featureFlags";
import formatPhoneNumber from "src/lib/formatPhoneNumber";
import reportErrorSentry from "src/lib/reportErrorSentry";
import LoadingCubit, { LoadingKey } from "src/state/LoadingCubit/LoadingCubit";
import type { UserData, UserState } from "src/state/UserCubit/UserState";
import {
  apiMiddleware,
  authenticationState,
  healthSyncState,
  programState,
  pushNotificationState,
  subscriptionState,
  tracker,
  userState
} from "src/state/state";
import { CustomQuestionnaireAnswer } from "../QuestionnaireCubit/QuestionnaireState";
import { StorageController } from "../StorageBloc/StorageBloc";
import { Blac } from "blac-next";
import UserPreferencesCubit from "../UserPreferencesCubit/UserPreferencesCubit";
import AppViewCubit from "../AppViewCubit/AppViewCubit";
import ConsistencyScoreCubit from "../ConsistencyScoreCubit/ConsistencyScoreCubit";
import RefillRequirementsCubit from "../RefillRequirementsCubit/RefillRequirementsCubit";

export type ProfileAttributeSelectedGoals =
  | "Get expert medical advice and treatment"
  | "Reduce or prevent adding medications"
  | "Improve my nutrition and create good habits"
  | "Lose weight and optimize my health"
  | "Sleep better and increase my daily energy";

export enum ProfileAttributesKeys {
  programMemberships = "program.memberships",
  programSelectedGoals = "program.user_selected_goals",
  partnerEmployersCurrent = "partner.employers.current",
  labsConditionGroups = "labs.condition-group",
  partnerPayersCurrent = "partner.payers.current",
  medicalConditions = "medical.conditions",
  hasCompletedInitialOrFollowup = "consultation.has-completed-initial-or-followup",
  phoneNumber = "auth.phone-number",
  memberPopulation = "partner.member_population",
  experienceGroup = "cohort.experience-group",
  primaryCondition = "medical.primary-condition-group",
  registeredAt = "auth.registered-at",
  medicationTransferIsInReview = "medicationtransfer.glp1.is-in-review",
  conciergeCare = "care.concierge-care"
}

export interface MedicalCondition {
  name: string;
  condition: {
    name: Condition;
    icd10: string;
  };
  abatement: string | null;
  onset?: string;
  isActive?: boolean;
}

export enum MemberPopulationType {
  DTC = "DTC",
  B2B = "B2B"
}

export interface MemberPopulation {
  name: string;
  type: "DTC" | "B2B";
}

export enum PayerTrait {
  ELIGIBLE_FOR_MEDICAL_BILLING = "ELIGIBLE_FOR_MEDICAL_BILLING",
  SUPPORTS_THIRD_PARTY_LAB_BILLING = "SUPPORTS_THIRD_PARTY_LAB_BILLING",
  REQUIRES_INITIAL_SYNC_VISIT = "REQUIRES_INITIAL_SYNC_VISIT"
}

interface Payer {
  active: boolean;
  start: string;
  end: string;
  partner: {
    id?: string;
    name: string;
    servedEmployerIds: string[];
    traits: PayerTrait[];
  };
  partnerId: string;
}

interface Employer {
  active: boolean;
  partner: {
    id: string;
    name: string;
    traits: [];
  };
  partnerId: string;
  start: string;
  end: string;
}

interface Membership {
  active: boolean;
  start: string;
  end: string;
  program?: ProfileProgramMembership;
}

export enum ComplexConditionGroup {
  HIGH_RISK = "High risk lab group",
  MODERATE_RISK = "Moderate risk lab group",
  LOW_RISK = "Low risk lab group"
}

export interface ProfileAttributes {
  [ProfileAttributesKeys.partnerEmployersCurrent]: Employer;
  [ProfileAttributesKeys.partnerPayersCurrent]: Payer;
  [ProfileAttributesKeys.programMemberships]: Membership[];
  [ProfileAttributesKeys.medicalConditions]: MedicalCondition[];
  [ProfileAttributesKeys.labsConditionGroups]: ComplexConditionGroup;
  [ProfileAttributesKeys.memberPopulation]: MemberPopulation;
  [ProfileAttributesKeys.programSelectedGoals]: {
    name: ProfileAttributeSelectedGoals;
    isPrimary: boolean;
  }[];
  [ProfileAttributesKeys.hasCompletedInitialOrFollowup]: boolean;
  [ProfileAttributesKeys.phoneNumber]: string;
  [ProfileAttributesKeys.experienceGroup]?: string;
  [ProfileAttributesKeys.primaryCondition]?: string;
  [ProfileAttributesKeys.registeredAt]?: string;
  [ProfileAttributesKeys.medicationTransferIsInReview]?: boolean;
  [ProfileAttributesKeys.conciergeCare]?: ConciergeCareAttribute;
}

/** Shape of the `care.concierge-care` profile attribute (matches PMP's `GET_CONCIERGE_CARE`). */
export interface ConciergeCareAttribute {
  prefersConciergeCare: boolean | null;
  hasEmployerWithOptionalConciergeCare: boolean;
}

const DEBUG_TC_SESSION_STORAGE_KEY = "debug-simulate-tc-session";

/** DEBUG ONLY: fake "current payer" attribute used to simulate a Transcarent member. */
const DEBUG_TRANSCARENT_PAYER: Payer = {
  active: true,
  start: "2024-01-01",
  end: "2099-01-01",
  partnerId: PayerId.TRANSCARENT,
  partner: {
    id: PayerId.TRANSCARENT,
    name: "Transcarent",
    servedEmployerIds: [],
    traits: []
  }
};

export default class UserCubit extends Cubit<UserState> {
  completedQuestionnairesMemoryCache = new Set<string>();

  constructor(initialState?: UserState) {
    super(initialState ?? {});

    window.addEventListener(globalEvents.USER_CLEAR, () => {
      this.emit({});
    });
  }

  reset = () => {
    this.emitAndCache({});
  };

  emitAndCache = (state: UserState): void => {
    this.emit(state);
  };

  hasAppAccess = (): boolean => {
    const state = authenticationState.state.authenticationStatus;
    const { userData } = this.state;
    if (
      state === "authenticated" &&
      userData &&
      (userData.email === undefined || userData.passwordSet === false)
    ) {
      return false;
    }

    // catch unauthenticated users
    if (state !== "authenticated") {
      return false;
    }

    return true;
  };

  populateUserprofileWithDemoData = async () => {
    // only allow on dev and qa backend
    const backendApiUrl = envVariables.API_BASE_URL;
    if (!backendApiUrl.includes(".dev.") && !backendApiUrl.includes(".qa.")) {
      return;
    }

    try {
      await DemoControllerService.populate();
    } catch (e) {
      reportErrorSentry(e);
    }
  };

  public get isUserRegistered(): boolean {
    return Boolean(this.state.userData?.email);
  }

  public get programMemberships() {
    return this.state.profileAttributes?.[
      ProfileAttributesKeys.programMemberships
    ];
  }

  public get medicalConditions() {
    return this.state.profileAttributes?.[
      ProfileAttributesKeys.medicalConditions
    ];
  }

  public get partnerPayer() {
    return this.state.profileAttributes?.[
      ProfileAttributesKeys.partnerPayersCurrent
    ];
  }

  public readonly hasProgramMembership = (
    program: ProfileProgramMembership
  ): boolean => {
    return (
      this.programMemberships?.find(
        (p) => p.program === program && p.active
      ) !== undefined
    );
  };

  public readonly setUserInfo = async (): Promise<void> => {
    const userData = await this.loadUserInfo();

    if (userData) {
      tracker.setAlias(userData.analyticsToken);
      this.emitAndCache({ ...this.state, userData });
      void this.loadMedications();
    }
  };

  fetchUserProfile = async (): Promise<UserProfileResponse> =>
    apiMiddleware.cached(
      async () => (await UserProfileControllerService.getUserProfile()).data
    );

  public readonly loadUserInfo = async (): Promise<UserData | undefined> => {
    apiMiddleware.clearAll();
    try {
      const data = await this.fetchUserProfile();

      const userData = {
        email: asTemplateValue(data.profile.email) as string | undefined,
        analyticsToken: asTemplateValue(data.profile.analyticsToken) as string,
        id: data.id,
        emailVerified: asTemplateValue(data.profile.emailVerified) as boolean,
        sessionAuthenticationMethod: asTemplateValue(
          data.profile.sessionAuthenticationMethod
        ) as string,
        passwordSet: Boolean(data.profile.passwordSet),
        mfaActive: Boolean(data.profile.mfaActive)
      } satisfies UserData;

      const finalUserData = this.applyDebugTcUserData(userData);

      this.emitAndCache({
        ...this.state,
        userData: finalUserData
      });

      const appViewState = Blac.getBloc(AppViewCubit);
      appViewState.setPartnerSession();

      return finalUserData;
    } catch (e: unknown) {
      reportErrorSentry(e);
    }
    return undefined;
  };

  public readonly loadSoleraUserInfo = async (): Promise<
    SoleraUserResponse | undefined
  > => {
    try {
      const response = await SoleraControllerService.retrieveUserInfo();
      if (!this.state.userData) {
        return;
      }

      return response.data;
    } catch (error: unknown) {
      reportErrorSentry(error);
    }
  };

  private readonly loadMedications = async (): Promise<
    MedicationsResponse | undefined
  > => {
    let result: MedicationsResponse | undefined = undefined;
    const loadingState = Blac.getBloc(LoadingCubit);
    loadingState.start(LoadingKey.medications);
    try {
      const response = await ProfileControllerService.getMedications();
      result = response.data;
      this.emitAndCache({ ...this.state, medications: result });
    } catch (e: unknown) {
      reportErrorSentry(e);
    } finally {
      loadingState.finish(LoadingKey.medications);
    }

    return result;
  };

  private fetchAnsweredQuestionnairesPromise:
    | [number, Promise<AnsweredQuestionnaire[]>]
    | [number, undefined] = [0, undefined];

  clearAnsweredQuestionnairesCache = () => {
    this.fetchAnsweredQuestionnairesPromise = [0, undefined];
  };

  fetchAnsweredQuestionnaires = async (): Promise<AnsweredQuestionnaire[]> => {
    let [cachedAt, promise] = this.fetchAnsweredQuestionnairesPromise;
    const now = Date.now();
    const cacheTime = 1000 * 60 * 1; // 1 minutes
    const cacheValid = now - cachedAt < cacheTime;

    if (!promise || !cacheValid) {
      promise = new Promise((resolve, reject) => {
        QuestionnaireControllerService.getAnsweredQuestionnaires()
          .then((res) => {
            resolve(res?.data);
          })
          .catch((e: unknown) => {
            reject(e as Error);
          });
      });

      cachedAt = now;
      this.fetchAnsweredQuestionnairesPromise = [cachedAt, promise];
    }

    try {
      return await promise;
    } catch (e: unknown) {
      reportErrorSentry(e);
      return [];
    }
  };

  public readonly loadUserAnsweredQuestionnaires = async (): Promise<
    AnsweredQuestionnaire[] | undefined
  > => {
    const loadingState = Blac.getBloc(LoadingCubit);
    loadingState.start(LoadingKey.answeredQuestionnaires);

    try {
      const answeredQuestionnaires = await this.fetchAnsweredQuestionnaires();

      addSentryBreadcrumb(
        "auth",
        `Successfully load user answered questionnaires`
      );

      this.emitAndCache({ ...this.state, answeredQuestionnaires });
    } catch (e: unknown) {
      reportErrorSentry(e);
    } finally {
      loadingState.finish(LoadingKey.answeredQuestionnaires);
    }

    return this.state.answeredQuestionnaires;
  };

  public readonly getBehavioralHealthQuestionnaireAnswers = async () => {
    await this.loadUserAnsweredQuestionnaires();
    const answeredQuestionnaires = this.state.answeredQuestionnaires ?? [];

    const phq = answeredQuestionnaires.filter(
      (questionnaire) =>
        questionnaire.questionnaireRef.id ===
        BEHAVIORAL_HEALTH_QUESTIONNAIRE_PHQ8
    );

    const phqData =
      phq.length > 0
        ? phq.reduce((latest, current) => {
            return new Date(current.timestamp) > new Date(latest.timestamp)
              ? current
              : latest;
          })
        : undefined;

    const gad = answeredQuestionnaires.filter(
      (questionnaire) =>
        questionnaire.questionnaireRef.id ===
        BEHAVIORAL_HEALTH_QUESTIONNAIRE_GAD7
    );

    const gadData =
      gad.length > 0
        ? gad.reduce((latest, current) => {
            return new Date(current.timestamp) > new Date(latest.timestamp)
              ? current
              : latest;
          })
        : undefined;

    const phqAnswers = phqData?.answers.json;
    const gadAnswers = gadData?.answers.json;

    return {
      phqAnswers: phqAnswers as CustomQuestionnaireAnswer[],
      gadAnswers: gadAnswers as CustomQuestionnaireAnswer[]
    };
  };

  public readonly getBinaryRatingQuestionnaireAnswers = async (
    subjectId: string
  ) => {
    await this.loadUserAnsweredQuestionnaires();
    const answeredQuestionnaires = this.state.answeredQuestionnaires ?? [];

    const binaryRatingQuestionnaires = answeredQuestionnaires.filter(
      (questionnaire) =>
        questionnaire.questionnaireRef.id ===
        PTHW_BINARY_RATING_QUESTIONNAIRE_ID
    );

    const binaryRating = binaryRatingQuestionnaires.find((questionnaire) =>
      ((questionnaire.answers?.json ?? []) as CustomQuestionnaireAnswer[]).find(
        (answer: CustomQuestionnaireAnswer) =>
          Array.isArray(answer?.fieldValue) &&
          answer?.fieldValue?.find(
            (field) =>
              field?.choiceId === "rating_subject_uid" &&
              field?.value === subjectId
          )
      )
    );

    return (binaryRating?.answers?.json ?? []) as CustomQuestionnaireAnswer[];
  };

  public readonly checkUserHasCompletedQuestionnaire__MEMORY = async (
    questionnaireId: string
  ): Promise<boolean> => {
    try {
      const sessItems = sessionStorage.getItem("completedQuestionnaires");
      if (sessItems) {
        const parsed = JSON.parse(sessItems) ?? [];
        if (Array.isArray(parsed)) {
          this.completedQuestionnairesMemoryCache = new Set(
            parsed
          ) as Set<string>;
        }
      }

      return this.completedQuestionnairesMemoryCache.has(questionnaireId);
    } catch (error) {
      reportErrorSentry(error);
    }
    return false;
  };

  public readonly checkUserHasAnsweredQuestionnaire = async (
    questionnaireId: string,
    loadAll?: boolean
  ): Promise<AnsweredQuestionnaire | undefined> => {
    if (loadAll !== false) {
      await this.loadUserAnsweredQuestionnaires();
    }

    const allQuestionnaires = this.state.answeredQuestionnaires ?? [];
    const sortedQuestionnaires = allQuestionnaires.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
    const thisQuestionnaire = sortedQuestionnaires.find(
      (q) => q.questionnaireRef.id === questionnaireId
    );

    return thisQuestionnaire;
  };

  readonly loadRequiredAppUserData = async (): Promise<void> => {
    apiMiddleware.clearAll();
    const userPreferences = Blac.getBloc(UserPreferencesCubit);
    try {
      const [userData, profileAttributes] = await Promise.all([
        this.loadUserInfo(),
        userState.getProfileAttributes(),
        subscriptionState.loadAllSubscriptions(),
        featureFlags.loadBackendFlags(),
        this.loadUserAnsweredQuestionnaires(),
        userPreferences.setupUserProperties(),
        programState.checkUserProfileProgramData(),
        this.getConsentStatus()
      ]);

      subscriptionState.checkForCoupon();

      // Kick off consistency score fetch early so dependent
      // features (e.g., GLP1 refill consolidation) have data ready.
      if (featureFlags.consistencyScoreEnabled) {
        const consistencyScoreState = Blac.getBloc(ConsistencyScoreCubit);
        await consistencyScoreState.fetchConsistencyScore();
      }

      // Refill decoupling (PROD-1264): the cubit's constructor fetch can run before
      // loadBackendFlags resolves and then short-circuit on the not-yet-loaded flag. Fetch
      // here, post-flags, so the refill card renders on first login instead of after a reload.
      if (featureFlags.refillDecouplingEnabled) {
        const refillRequirementsState = Blac.getBloc(RefillRequirementsCubit);
        refillRequirementsState.refresh();
      }

      if (StorageController.getItem("healthActive") === "true") {
        void healthSyncState.checkAndSync();
      }

      let setNewState = false;
      const newState: typeof this.state = {
        ...this.state
      };

      if (userData) {
        newState.userData = userData;
        setNewState = true;
        tracker.identifyUserByTrackingId(userData.analyticsToken);
        void pushNotificationState.init();
      }

      if (profileAttributes) {
        newState.profileAttributes = profileAttributes;
        setNewState = true;
      }

      if (setNewState) {
        this.emitAndCache(newState);
      }
    } catch (e: unknown) {
      reportErrorSentry(e);
    }
  };

  static setNotificationNumber = async (
    phoneNumber: string,
    options: {
      showLoading?: boolean;
    } = {}
  ): Promise<{ error: string }> => {
    apiMiddleware.clearAll();
    let error = "";
    const loadingState = Blac.getBloc(LoadingCubit);
    try {
      if (options.showLoading !== false) {
        loadingState.start(LoadingKey.treatmentPlan);
      }
      const number = formatPhoneNumber(phoneNumber);

      if (number) {
        await SmsControllerService.registerPhoneNumber({
          phoneNumber: number,
          verify: false
        });
      }
      addSentryBreadcrumb("auth", `Successfully set notification number`);
    } catch (e: unknown) {
      reportErrorSentry(e);
      error = extractErrorCode(e);
    } finally {
      if (options.showLoading !== false) {
        loadingState.finish(LoadingKey.treatmentPlan);
      }
    }
    return { error };
  };

  private getUserMedication(
    medication: MedicationsResponseItem.medication
  ): MedicationsResponseItem | undefined {
    const medicationList = this.state.medications?.medications ?? [];
    return medicationList.find((item) => item.medication === medication);
  }

  get userIsOnInsulin(): boolean {
    const insulin = this.getUserMedication(
      MedicationsResponseItem.medication.INSULIN
    );
    return Boolean(insulin);
  }

  getProfileAttributes = async (
    attributes: ProfileAttributesKeys[] = [
      ProfileAttributesKeys.phoneNumber,
      ProfileAttributesKeys.partnerEmployersCurrent,
      ProfileAttributesKeys.partnerPayersCurrent,
      ProfileAttributesKeys.programMemberships,
      ProfileAttributesKeys.medicalConditions,
      ProfileAttributesKeys.labsConditionGroups,
      ProfileAttributesKeys.programSelectedGoals,
      ProfileAttributesKeys.memberPopulation,
      ProfileAttributesKeys.experienceGroup,
      ProfileAttributesKeys.hasCompletedInitialOrFollowup,
      ProfileAttributesKeys.primaryCondition,
      ProfileAttributesKeys.registeredAt,
      ProfileAttributesKeys.conciergeCare
    ]
  ): Promise<ProfileAttributes | undefined> => {
    try {
      const res =
        await ProfileControllerService.getMyProfileAttributeValues(attributes);
      const data = this.applyDebugTcProfileAttributes(
        res.data as unknown as ProfileAttributes
      ) as ProfileAttributes;
      this.emit({
        ...this.state,
        profileAttributes: data
      });
      return data;
    } catch (e: unknown) {
      reportErrorSentry(e);
      return undefined;
    }
  };

  fetchAndMergeProfileAttributes = async (
    attributes: ProfileAttributesKeys[]
  ): Promise<ProfileAttributes | undefined> => {
    try {
      const res =
        await ProfileControllerService.getMyProfileAttributeValues(attributes);
      const data = res.data as unknown as Partial<ProfileAttributes>;
      const merged = {
        ...this.state.profileAttributes,
        ...data
      } as ProfileAttributes;
      this.emit({
        ...this.state,
        profileAttributes: merged
      });
      return merged;
    } catch (e: unknown) {
      reportErrorSentry(e);
      return undefined;
    }
  };

  checkProfileAttributesCurrentProgramMembership = async (): Promise<
    ProfileProgramMembership[] | false
  > => {
    const data =
      this.state.profileAttributes?.[ProfileAttributesKeys.programMemberships];

    // check if data exists
    if (typeof data === "undefined" || data.length === 0) {
      return false;
    }

    //   collect programs
    const programs: ProfileProgramMembership[] = [];
    data.forEach((item) => {
      if (item.active && item.program) {
        programs.push(item.program);
      }
    });

    return programs;
  };

  public readonly setProgramMemberships = (
    program: ProfileProgramMembership
  ) => {
    if (this.state.profileAttributes) {
      this.emit({
        ...this.state,
        profileAttributes: {
          ...this.state.profileAttributes,
          [ProfileAttributesKeys.programMemberships]: [
            {
              active: true,
              start: "",
              end: "",
              program
            }
          ]
        }
      });
    }
  };

  /******************** */
  // Consent Management //
  /******************** */

  shouldIgnoreConsentUpdate = (consent: ConsentStatusResponse) => {
    const ignoreConsentTypes = [
      ConsentStatusResponse.consent.HEALTH_INFORMATION_RELEASE
    ];
    const ignoreConsentVersions: Partial<
      Record<ConsentStatusResponse.consent, number>
    > = {
      [ConsentStatusResponse.consent.TERMS_OF_SERVICE]: 20240903
    };

    const ignoreConsent = ignoreConsentTypes.includes(consent.consent);
    const ignoreConsentVersion =
      ignoreConsentVersions[consent.consent] === consent.version;

    return ignoreConsent || ignoreConsentVersion;
  };

  getConsentStatus = async (): Promise<void> => {
    try {
      const result = await ConsentControllerService.listConsentStatuses();
      const consentStatus = result.data;
      this.emitAndCache({
        ...this.state,
        consentStatus
      });
    } catch (error) {
      reportErrorSentry(error);
    }
  };

  acceptConsents = async (): Promise<void> => {
    if (
      this.state.consentStatus === undefined ||
      this.state.consentStatus.length === 0
    ) {
      await this.getConsentStatus();
    }

    const newStatuses = this.state.consentStatus?.map((c) => ({
      ...c,
      status: ConsentStatusResponse.status.ACCEPTED
    }));

    try {
      await ConsentControllerService.updateConsentStatus(
        newStatuses as ConsentStatusUpdateRequest[]
      );
      this.emitAndCache({
        ...this.state,
        consentStatus: newStatuses
      });
    } catch (error: unknown) {
      reportErrorSentry(error);
    }
  };

  get hasConsentsNotAccepted(): boolean | undefined {
    if (typeof this.state.consentStatus === "undefined") {
      return undefined;
    }

    const consents = this.state.consentStatus.filter(
      (con) => !this.shouldIgnoreConsentUpdate(con)
    );

    const justStatus = consents.map((c) => c.status);
    const allAccepted = justStatus.every(
      (s) => s === ConsentStatusResponse.status.ACCEPTED
    );

    return !allAccepted;
  }

  /**
   * True only when the session auth method is explicitly ANONYMOUS. Determined
   * purely from the session auth method (the source of truth from `/me`) — it
   * does not infer anything from email/password state.
   */
  get isAnonymousSession(): boolean {
    return (
      this.state.userData?.sessionAuthenticationMethod ===
      AuthenticationMethod.ANONYMOUS
    );
  }

  /**
   * True when the user authenticated via an external SSO provider (Google or a
   * SAML/Transcarent identity provider). Based on the session auth method
   * rather than inferring from email-verified + no-password state.
   */
  get userIsSso(): boolean {
    const method = this.state.userData?.sessionAuthenticationMethod;
    return (
      method === AuthenticationMethod.GOOGLE ||
      method === AuthenticationMethod.SAML2 ||
      method === AuthenticationMethod.TRANSCARENT_SSO
    );
  }

  get partnerId(): string | undefined {
    return this.state.profileAttributes?.["partner.payers.current"]?.partnerId;
  }

  get isTranscarentMember(): boolean {
    return Boolean(this.partnerId === PayerId.TRANSCARENT);
  }

  private get debugTcSessionEnabled(): boolean {
    try {
      return sessionStorage.getItem(DEBUG_TC_SESSION_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  }

  /**
   * DEBUG: force a Transcarent SSO login shape onto userData when the debug flag
   * is on. A real SSO member arrives with a verified email (from the IdP) and no
   * password, so we seed both for realism alongside the SSO session auth method.
   */
  private applyDebugTcUserData(userData: UserData): UserData {
    if (!this.debugTcSessionEnabled) {
      return userData;
    }
    return {
      ...userData,
      email: userData.email ?? "transcarent-debug@9am.health",
      emailVerified: true,
      sessionAuthenticationMethod: AuthenticationMethod.TRANSCARENT_SSO,
      passwordSet: false
    };
  }

  /** DEBUG: inject the Transcarent payer attribute when the debug flag is on. */
  private applyDebugTcProfileAttributes(
    attributes?: ProfileAttributes
  ): ProfileAttributes | undefined {
    if (!this.debugTcSessionEnabled) {
      return attributes;
    }
    return {
      ...(attributes ?? {}),
      [ProfileAttributesKeys.partnerPayersCurrent]: DEBUG_TRANSCARENT_PAYER
    } as ProfileAttributes;
  }

  /**
   * DEBUG ONLY: simulate a member authenticated via Transcarent SSO + OTT
   * exchange, without real SSO or response overrides. Persists a sessionStorage
   * flag so the override is re-applied on every profile/attribute load (survives
   * reload). Does NOT seed name preferences.
   *
   * enable  -> set flag + apply override to current in-memory state.
   * disable -> clear flag + refetch real profile/attributes to restore.
   */
  simulateTranscarentSession = (enabled: boolean): void => {
    try {
      if (enabled) {
        sessionStorage.setItem(DEBUG_TC_SESSION_STORAGE_KEY, "true");
      } else {
        sessionStorage.removeItem(DEBUG_TC_SESSION_STORAGE_KEY);
      }
    } catch {
      // sessionStorage unavailable — best-effort only
    }

    if (enabled) {
      this.emit({
        ...this.state,
        userData: this.state.userData
          ? this.applyDebugTcUserData(this.state.userData)
          : this.state.userData,
        profileAttributes: this.applyDebugTcProfileAttributes(
          this.state.profileAttributes
        )
      });
    } else {
      void this.loadUserInfo();
      void this.getProfileAttributes();
    }
  };

  get isStateOfGeorgiaMember(): boolean {
    return Boolean(
      this.state.profileAttributes?.["partner.payers.current"]?.partnerId ===
      PayerId.GEORGIA
    );
  }

  isPayerMember(payerId: string): boolean {
    return (
      this.state.profileAttributes?.["partner.payers.current"]?.partnerId ===
      payerId
    );
  }

  isEmployerMember(employerId: string): boolean {
    return (
      this.state.profileAttributes?.["partner.employers.current"]?.partnerId ===
      employerId
    );
  }

  get payerAdditionalLegalTextKey() {
    const payerId = this.partnerId as PayerId | undefined;
    return payerId ? PayerAdditionalLegalTextKey[payerId] : undefined;
  }

  get labsConditionGroup(): ComplexConditionGroup | undefined {
    return this.state.profileAttributes?.[
      ProfileAttributesKeys.labsConditionGroups
    ];
  }
}
